import { getChatCompletion, getStructuredResponse, tools } from '@worktango/ai-assistant/react';
import { fetchCurrentAccountQuery } from '~/behaviors/react-query/account';
import { fetchSurveyElementsBySurveysQuery } from '~/behaviors/react-query/surveyElements';
import { fetchBasicSurveyListQuery } from '~/behaviors/react-query/survey';
import { commentsQuery } from '~/behaviors/react-query/comments';
import { resolvePeopleLeaderFilter } from './helpers/resolvePeopleLeaderFilter';

const DEFAULT_INSTRUCTIONS =
  'Summarize the comments and provide of the most relevant ones. Also highlight individual comments that are not part of the majority or group of comments.';

export const questionCommentsAnalysisTool = tools.createTool({
  name: 'question_comments_analysis',
  description: 'Analyze the comments of a question',
  displayName: 'Analyzing question comments',
  pastTenseDisplayName: 'Analyzed question comments',
  /* istanbul ignore start -- parameter definitions are not executed during tests (tools.createTool is mocked) */
  parameters: (t) =>
    t.Object({
      question: t.Object({
        questionId: t.Optional(
          t.Number({
            description:
              'The id of the question. Providing this alone is sufficient. Other tools may expose this as the question `id`.',
          }),
        ),
        surveyElementId: t.Optional(
          t.Number({ description: 'The id of the survey element. Providing this alone is sufficient.' }),
        ),
        questionTitle: t.Optional(
          t.String({
            description: 'The title of the question. Required only if no questionId or surveyElementId is provided.',
          }),
        ),
      }),
      survey: t.Object({
        surveyId: t.Optional(t.Number({ description: 'The id of the survey to get the questions and comments from' })),
        surveyTitle: t.Optional(
          t.String({ description: 'The title of the survey. It is required if the surveyId is not provided.' }),
        ),
      }),
      userInstructions: t.Optional(
        t.String({
          description: 'The instructions provided by the user on how to handle the results',
          default: DEFAULT_INSTRUCTIONS,
        }),
      ),
      attributeFilters: t.Optional(
        t.Array(
          t.Object({
            id: t.Number({ description: 'Attribute ID to filter by' }),
            values: t.Array(t.String({ description: 'Attribute values to include' })),
          }),
          {
            description:
              "Optional filters to constrain the comments to specific attribute values. To filter for a people leader's team, pass the people leader attribute ID with the person's full name as the value.",
          },
        ),
      ),
      peopleLeaderAttributeId: t.Optional(
        t.Number({
          description: 'The id of the attribute which contains the people leader ids',
        }),
      ),
      isHierarchyReport: t.Optional(
        t.Boolean({
          description:
            'REQUIRED when filtering by people leader/manager: whether to include the full management hierarchy (true = all subordinates at all levels) or only direct reports (false = immediate reports only).',
        }),
      ),
    }),
  /* istanbul ignore stop */
  fulfill: async (args) => {
    try {
      const { userInstructions, question, survey, attributeFilters, peopleLeaderAttributeId, isHierarchyReport } = args;
      const instructions = userInstructions ? userInstructions : DEFAULT_INSTRUCTIONS;

      const hasManagerFilter = attributeFilters?.some((filter) => filter.id === peopleLeaderAttributeId);
      if (hasManagerFilter && typeof isHierarchyReport !== 'boolean') {
        return JSON.stringify({
          error: `Manager hierarchy data detected in filters. You MUST ask the user to specify:
- "Do you want direct reports only?" (set isHierarchyReport: false)
- "Do you want full management hierarchy?" (set isHierarchyReport: true)
Wait for their explicit choice before proceeding.`,
        });
      }

      const { account } = await fetchCurrentAccountQuery();

      const surveyId = await getSurveyId({
        companyId: account.companyId,
        surveyId: survey.surveyId,
        surveyTitle: survey.surveyTitle,
      });

      const { questionId, surveyElementId } = await getQuestionIds({
        surveyId,
        questionId: question.questionId,
        surveyElementId: question.surveyElementId,
        questionTitle: question.questionTitle,
      });

      const peopleLeaderFilter = await resolvePeopleLeaderFilter({
        companyId: account.companyId,
        languageId: account.langId,
        surveyIds: [surveyId],
        peopleLeaderAttributeId: peopleLeaderAttributeId ?? null,
        attributeFilters,
        isHierarchyReport,
      });

      // Without this the request would run unfiltered and the summary would cover the whole survey
      // while being presented to the user as that leader's team.
      if (peopleLeaderFilter.error) {
        return JSON.stringify({ error: peopleLeaderFilter.error });
      }

      const commentsSummary = await summarizeComments({
        companyId: account.companyId,
        surveyId,
        questionId,
        surveyElementId,
        userInstructions: instructions,
        managerReportIds: peopleLeaderFilter.managerReportIds,
        isHierarchyReport,
      });

      return JSON.stringify(commentsSummary);
    } catch (error) {
      console.error(error);
      return `Error: ${error}`;
    }
  },
});

type QuestionTranslation = { languageId: number; title: string };

function toTranslationList(translations?: Record<string, QuestionTranslation>): QuestionTranslation[] {
  return Object.values(translations ?? {}).map(({ languageId, title }) => ({ languageId, title }));
}

async function getQuestionIds({
  surveyId,
  questionId,
  surveyElementId,
  questionTitle,
}: {
  surveyId: number;
  questionId?: number;
  surveyElementId?: number;
  questionTitle?: string;
}): Promise<{ questionId: number; surveyElementId: number }> {
  if (questionId && surveyElementId) {
    return { questionId, surveyElementId };
  }

  if (!questionId && !surveyElementId && !questionTitle) {
    throw new Error(
      'No question details were provided. Please provide a question id, survey element id, or question title.',
    );
  }

  const surveyWithSurveyElements = await fetchSurveyElementsBySurveysQuery([surveyId]);
  const surveyElements = surveyWithSurveyElements[0].surveyElements;

  if (surveyElements.length === 0) {
    throw new Error(`No survey elements were found for survey ${surveyId}.`);
  }

  // A single id often arrives under either name: other coach tools (e.g. survey aggregated results)
  // surface a question's surveyElementId as its `id`, so callers pass that value as questionId here.
  // A surveyElementId must match an element's id exactly; only the questionId param is allowed to
  // fall back to the questionId field, since matching a stale surveyElementId against questionId
  // could silently resolve to the wrong question.
  let matchedById: (typeof surveyElements)[number] | undefined;
  if (surveyElementId !== undefined) {
    matchedById = surveyElements.find((element) => element.id === surveyElementId);
  } else if (questionId !== undefined) {
    matchedById =
      surveyElements.find((element) => element.id === questionId) ??
      surveyElements.find((element) => element.questionId === questionId);
  }

  if (surveyElementId !== undefined || questionId !== undefined) {
    if (!matchedById) {
      throw new Error(`No question matching the provided id was found in survey ${surveyId}.`);
    }

    return { questionId: matchedById.questionId, surveyElementId: matchedById.id };
  }

  const compactQuestions = surveyElements.map((element) => ({
    surveyElementId: element.id,
    title: element.title,
    translations: toTranslationList(element.translations),
  }));

  const matches = await getStructuredResponse({
    temperature: 0.25,
    model: 'medium',
    schema: (t) =>
      t.Array(
        t.Object({
          surveyElementId: t.Number(),
        }),
      ),
    prompt: `
Your role is to find the question that matches the question title.

<instructions>
- You will always return an array of questions.
- Try to return the most specific question.
- If in doubt between multiple questions, return all of them.
- If you don't find the question, return an empty array.
</instructions>

<question_title>
${questionTitle}
</question_title>

<questions>
${JSON.stringify(compactQuestions, null, 2)}
</questions>
    `,
  });

  if (matches.length === 0) {
    throw new Error(`No question details were found for ${questionTitle}`);
  }

  if (matches.length > 1) {
    throw new Error(`Multiple questions were found for ${questionTitle}.
      Tell the user to pick one of them.

      <questions>
      ${JSON.stringify(matches, null, 2)}
      </questions>
      `);
  }

  // Resolve the authoritative ids from the survey elements rather than trusting the model to
  // echo them back. The comments query requires non-null questionId and surveyElementId, so a
  // hallucinated or missing value would fail the request instead of returning no comments.
  const matchedElement = surveyElements.find((element) => element.id === matches[0].surveyElementId);

  if (!matchedElement) {
    throw new Error(`No question details were found for ${questionTitle}`);
  }

  return { questionId: matchedElement.questionId, surveyElementId: matchedElement.id };
}

async function getSurveyId({
  companyId,
  surveyId,
  surveyTitle,
}: {
  companyId: number;
  surveyId?: number;
  surveyTitle?: string;
}) {
  if (surveyId) {
    return surveyId;
  }

  if (!surveyTitle) {
    throw new Error('No survey details were provided. Please provide at least a survey title.');
  }

  const companySurveys = await fetchBasicSurveyListQuery(companyId);

  if (companySurveys.length === 0) {
    throw new Error(`No surveys were found for company ${companyId}.`);
  }

  const surveys = await getStructuredResponse({
    temperature: 0.25,
    model: 'medium',
    schema: (t) =>
      t.Array(
        t.Object({
          surveyId: t.Number(),
          name: t.String(),
        }),
      ),
    prompt: `
Your role is to find the survey that matches the survey name.

<instructions>
- You will always return an array of surveys.
- Try to return the most specific survey.
- If in doubt between multiple surveys, return all of them.
- If you don't find the survey, return an empty array.
</instructions>

<survey_name>
${surveyTitle}
</survey_name>

<surveys>
${JSON.stringify(companySurveys, null, 2)}
</surveys>
`,
  });

  if (surveys.length === 0) {
    throw new Error(`No surveys were found for ${surveyTitle}.`);
  }

  if (surveys.length > 1) {
    throw new Error(`Multiple surveys were found for ${surveyTitle}.
      Tell the user to pick one of them.

      <surveys>
      ${JSON.stringify(surveys, null, 2)}
      </surveys>
      `);
  }

  return surveys[0].surveyId;
}

async function summarizeComments({
  companyId,
  surveyId,
  questionId,
  surveyElementId,
  userInstructions,
  managerReportIds,
  isHierarchyReport,
}: {
  companyId: number;
  surveyId: number;
  questionId: number;
  surveyElementId: number;
  userInstructions: string;
  managerReportIds?: string[];
  isHierarchyReport?: boolean;
}) {
  // 150 comments
  // companyId: 88,
  // surveyIds: [1936],
  // questionId: 1478,
  // surveyElementId: 10877,

  // 480 comments
  // companyId: 88,
  // surveyIds: [1937],
  // questionId: 1477,
  // surveyElementId: 10862,

  // 1000 comments
  // companyId: 258,
  // surveyIds: [5743],
  // questionId: 5199,
  // surveyElementId: 36753,

  // 4000 comments
  // companyId: 258,
  // surveyIds: [5743],
  // questionId: 5206,
  // surveyElementId: 36760,

  const result: { comments: { comments: any[]; commentCount: number } } = await commentsQuery({
    companyId,
    surveyIds: [surveyId],
    questionId,
    surveyElementId,
    searchText: null,
    limit: 0,
    offset: 0,
    keyword: null,
    sentimentFilter: null,
    themeFilter: null,
    entityFilter: null,
    managerReportIds: managerReportIds && managerReportIds.length > 0 ? managerReportIds : null,
    isHierarchyReport: managerReportIds && managerReportIds.length > 0 ? Boolean(isHierarchyReport) : null,
    sort: {
      column: null,
      order: 'asc',
    },
  });

  const { comments, commentCount } = result.comments;

  if (commentCount === 0) {
    return {
      error: `No comments were found for the given parameters.`,
      summary: null,
    };
  }

  const compactComments = comments.map((comment) => ({
    text: comment.text,
    sentiment: comment.sentiment,
    themes: comment.themes,
    like: comment.like,
    greatIdea: comment.greatIdea,
    workingOnIt: comment.workingOnIt,
    reactedLike: comment.reactedLike,
    reactedGreatIdea: comment.reactedGreatIdea,
    reactedWorkingOnIt: comment.reactedWorkingOnIt,
  }));

  const commentsString = JSON.stringify(compactComments, null, 2);
  // TODO: Remove this if we really decide to go with the large model for all content.
  // const contentSize = commentsString.length;

  // const maxMediumModelTokens = 50000;
  // const charsPerToken = 4;
  // const extraChars = 1; // extra chars to take into account things like spaces, new lines, etc.

  // const maxChars = maxMediumModelTokens * (charsPerToken + extraChars);

  const commentsSummary = await getChatCompletion({
    temperature: 0.25,
    model: 'medium',
    stream: false,
    systemInstruction: `
    You are a helpful assistant that summarizes comments.

    <instructions>
    ${userInstructions}
    </instructions>
    `,
    messages: [
      {
        role: 'user',
        parts: [
          {
            type: 'text',
            text: `Here are the comments.

          <comments>
          ${commentsString}
          </comments>
          `,
          },
        ],
      },
    ],
  });

  if (commentsSummary[0].type !== 'text') {
    return 'Error: The summary returned in an unexpected format. Please try again.';
  }

  return commentsSummary[0].text;
}
